import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import getHairlineColor from "components/charts/modern/overlay/getHairlineColor";
import Avatar from "components/core/Avatar";
import { getPlatformVerticalTitle } from "components/financials/modern/utils/platformVertical";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/Dialog";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import { AnimatePresence, motion } from "framer-motion";

import { useAmplitude } from "lib/amplitude/Amplitude";
import type { ICompanyIssuerFieldsFragment } from "lib/graphql/fragments/__generated__/CompanyIssuerFields.generated";
import RegisterUserInteractionMutation from "lib/graphql/mutations/RegisterUserInteractionMutation";
import type {
  IRegisterUserInteractionMutation,
  IRegisterUserInteractionMutationVariables,
} from "lib/graphql/mutations/__generated__/RegisterUserInteractionMutation.generated";
import RelatedPeersQuery from "lib/graphql/queries/RelatedPeersQuery";
import SearchIssuersByTickerSymbolQuery from "lib/graphql/queries/SearchIssuersByTickerSymbolQuery";
import type {
  IRelatedPeersQuery,
  IRelatedPeersQueryVariables,
} from "lib/graphql/queries/__generated__/RelatedPeersQuery.generated";
import type {
  ISearchIssuersByTickerSymbolQuery,
  ISearchIssuersByTickerSymbolQueryVariables,
} from "lib/graphql/queries/__generated__/SearchIssuersByTickerSymbolQuery.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import debounce from "lodash/debounce";
import uniqBy from "lodash/uniqBy";
import { Loader2, SearchIcon, XIcon } from "lucide-react";
import type { ReactNode } from "react";
import { forwardRef, startTransition, useCallback, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { FormattedMessage, useIntl } from "react-intl";
import { IPlatformVertical } from "types/graphqlTypes";

export type SelectedCompanyIssuer = Omit<ICompanyIssuerFieldsFragment, "__typename">;

interface IssuerByTickerSymbolSearchProps {
  vertical?: IPlatformVertical;
  tickerSymbol?: string;

  disabled?: boolean;

  // Verbiage stuff...
  title?: ReactNode;
  description?: ReactNode;
  inputSearchPlaceholder?: string;
  inputSearchNoResultsText?: string;

  selectedIssuers?: SelectedCompanyIssuer[];
  children: ReactNode;

  onChange: (issuer: ICompanyIssuerFieldsFragment) => void;
}
export default function IssuerByTickerSymbolSearch({
  vertical,
  title,
  description,
  inputSearchPlaceholder,
  inputSearchNoResultsText,
  disabled,
  tickerSymbol,
  children,
  selectedIssuers,
  onChange,
}: IssuerByTickerSymbolSearchProps) {
  const intl = useIntl();
  const amplitude = useAmplitude();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<ICompanyIssuerFieldsFragment[]>([]);
  const { data, loading } = useQuery<IRelatedPeersQuery, IRelatedPeersQueryVariables>(RelatedPeersQuery, {
    skip: !isDialogOpen || !tickerSymbol,
    variables: {
      tickerSymbol,
      filterByVertical: vertical || null,
    },
  });

  const [registerUserInteraction] = useMutation<
    IRegisterUserInteractionMutation,
    IRegisterUserInteractionMutationVariables
  >(RegisterUserInteractionMutation, {
    errorPolicy: "ignore",
  });

  useHotkeys(
    "ctrl+f, meta+f, f3",
    (event) => {
      if (!isDialogOpen && !disabled) {
        event.preventDefault();
        setIsDialogOpen(true);
      }
    },
    {
      preventDefault: true,
      ignoreModifiers: false,
    },
  );

  const peers =
    [
      data?.relatedPeers?.competitors || [],
      data?.relatedPeers?.comparables || [],
      data?.relatedPeers?.industryPeers || [],
    ].find((group) => group.length > 0) || [];
  const options = uniqBy(
    [...searchResults, ...peers.map((node) => node.issuer).filter(notUndefinedOrNull)].filter((candidate) => {
      return tickerSymbol ? candidate.tickerSymbol !== tickerSymbol.toUpperCase() : true;
    }),
    (issuer) => issuer._id,
  );

  return (
    <Dialog open={isDialogOpen} onOpenChange={(isOpen) => setIsDialogOpen(isOpen)}>
      {children}
      <DialogContent className="rounded-none border-slate-300 bg-white dark:bg-slate-950 sm:rounded-3xl">
        <DialogHeader className="space-y-1 text-left">
          {title ? (
            <DialogTitle>{title}</DialogTitle>
          ) : (
            <DialogTitle>
              {vertical
                ? intl.formatMessage(
                    {
                      defaultMessage: "Pesquise {vertical}",
                      id: "L740+x",
                      description: "Issuer search dialog title",
                    },
                    {
                      vertical: getPlatformVerticalTitle(vertical),
                    },
                  )
                : intl.formatMessage({
                    defaultMessage: "Pesquise ativos",
                    id: "3outgA",
                    description: "Issuer search dialog title",
                  })}
            </DialogTitle>
          )}
          <DialogDescription className="text-sm italic tracking-wide text-slate-500 dark:text-slate-400">
            {description || getDescription(vertical)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <IssuerSearchInput
            disabled={disabled}
            placeholder={
              inputSearchPlaceholder ||
              intl.formatMessage({
                defaultMessage: "Digite um ticker completo...",
                id: "LtCSPT",
                description: "Issuer search input placeholder text",
              })
            }
            noResultsText={
              inputSearchNoResultsText ||
              intl.formatMessage({
                defaultMessage: "Nada encontrado...",
                description: "Issuer search no results text",
                id: "tlrkC1",
              })
            }
            onChange={setSearchResults}
            onConfirmSearch={(issuer) => {
              setIsDialogOpen(false);
              startTransition(() => {
                amplitude.logEvent("Confirmed issuer search", {
                  vertical: issuer.vertical,
                  tickerSymbol: issuer.tickerSymbol,
                  origin: "Search Input",
                });
                registerUserInteraction({
                  variables: {
                    input: {
                      kind: "SELECTED_ISSUER",
                      payload: {
                        issuerId: issuer._id,
                        tickerSymbol: issuer.tickerSymbol,
                        vertical: issuer.vertical,
                        exchangeId: issuer.exchangeId,
                        name: issuer.name,
                      },
                    },
                  },
                });
                onChange(issuer);
              });
            }}
            queryVariables={{
              vertical,
            }}
          />

          {disabled && (
            <p
              className={classNames(
                "text-xs font-medium italic leading-5 tracking-wide text-slate-700",
                "rounded-lg",
                "bg-orange-50/60 px-3 py-1 text-orange-600 dark:bg-orange-600/10 dark:text-orange-300",
              )}
            >
              <FormattedMessage defaultMessage="Você atingiu o limite de ativos..." id="74ZLgR" />
            </p>
          )}

          <div className="-mx-2 flex flex-col items-start empty:hidden">
            {tickerSymbol && loading && (
              <div className="w-full animate-pulse space-y-4 px-2">
                <div className="flex h-12 w-full rounded-lg bg-slate-100 outline-none ring-1 ring-slate-200 ring-offset-2 ring-offset-white" />
                <div className="flex h-12 w-full rounded-lg bg-slate-100 outline-none ring-1 ring-slate-200 ring-offset-2 ring-offset-white" />
                <div className="flex h-12 w-full rounded-lg bg-slate-100 outline-none ring-1 ring-slate-200 ring-offset-2 ring-offset-white" />
                <div className="flex h-12 w-full rounded-lg bg-slate-100 outline-none ring-1 ring-slate-200 ring-offset-2 ring-offset-white" />
              </div>
            )}

            <AnimatePresence initial={false}>
              {!loading &&
                options.map((issuer) => {
                  const isSelected = selectedIssuers?.some((candidate) => {
                    return candidate._id === issuer._id;
                  });
                  const hairlineColor = getHairlineColor(issuer, true);
                  return (
                    <motion.button
                      type="button"
                      key={issuer._id}
                      initial={{ opacity: 0, y: 0 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 0 }}
                      disabled={loading || isSelected}
                      className={classNames(
                        "group my-1.5 flex w-full flex-1 items-center space-x-3 rounded-lg px-2 py-1",
                        "ring-1 ring-transparent ring-offset-2 ring-offset-transparent focus-visible:outline-none",
                        "focus-visible:bg-slate-50 focus-visible:ring-slate-200 focus-visible:ring-offset-white",
                        {
                          "bg-violet-50 ring-1 ring-violet-200 ring-offset-2 ring-offset-white": isSelected,
                          "dark:bg-violet-950/40 dark:ring-violet-950/0 dark:ring-offset-violet-950/0": isSelected,
                          "focus-visible:bg-violet-50 focus-visible:ring-violet-200 focus-visible:ring-offset-white":
                            isSelected,
                        },
                        "hover:bg-slate-50 hover:ring-slate-200 hover:ring-offset-white",
                        "disabled:pointer-events-none",
                        "dark:hover:bg-slate-800 dark:hover:ring-slate-800 dark:hover:ring-offset-slate-800",
                      )}
                      onClick={() => {
                        amplitude.logEvent("Confirmed issuer search", {
                          vertical: issuer.vertical,
                          tickerSymbol: issuer.tickerSymbol,
                          origin: "Options",
                        });
                        onChange(issuer);
                        if (options.length === 1) {
                          setIsDialogOpen(false);
                        }
                      }}
                    >
                      <Avatar
                        src={issuer.niceLogo?.url || null}
                        hashValue={issuer._id}
                        alt={issuer.name}
                        size={42}
                        squircle={true}
                        className="dark:invert-100"
                      />

                      <div className="w-full py-0.5 text-left">
                        <strong className="text-sm font-bold tracking-tight text-slate-800 dark:text-white">
                          {issuer.name}
                        </strong>
                      </div>

                      <div className="ml-auto w-full space-x-3 text-right">
                        <span
                          className={classNames(
                            "transition-none duration-100 ease-in-out",
                            "rounded-xl px-2 py-1 text-xs tracking-wider dark:bg-gray-700 dark:text-gray-300",
                            "bg-violet-50 font-mono font-semibold text-violet-600 group-hover:bg-violet-100",
                            "group-focus-visible:bg-slate-500 group-focus-visible:text-white",
                            "dark:group-hover:bg-violet-800/20 dark:group-hover:text-violet-200",
                          )}
                        >
                          {issuer.tickerSymbol}
                        </span>

                        <span
                          aria-hidden={true}
                          className={classNames(
                            "relative inline-flex h-2.5 w-2.5 items-center justify-center rounded-full bg-violet-700 opacity-40",
                            "group-hover:opacity-80",
                          )}
                          style={{ backgroundColor: hairlineColor || undefined }}
                        />
                      </div>
                    </motion.button>
                  );
                })}
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface IssuerSearchInputProps {
  debounceMs?: number;
  placeholder?: string;
  noResultsText?: string;
  queryVariables?: Partial<Omit<ISearchIssuersByTickerSymbolQueryVariables, "tickerSymbol">>;
  disabled?: boolean;
  onChange: (results: ISearchIssuersByTickerSymbolQuery["searchIssuersByTickerSymbol"]) => void;
  onConfirmSearch?: (result: ISearchIssuersByTickerSymbolQuery["searchIssuersByTickerSymbol"][0]) => void;
}
function IssuerSearchInput({
  debounceMs = 200,
  placeholder,
  noResultsText = "Nada...",
  disabled = false,
  queryVariables,
  onChange,
  onConfirmSearch,
}: IssuerSearchInputProps) {
  const [performSearch, { loading, data, called, variables }] = useLazyQuery<
    ISearchIssuersByTickerSymbolQuery,
    ISearchIssuersByTickerSymbolQueryVariables
  >(SearchIssuersByTickerSymbolQuery, {
    onCompleted(data) {
      if (data && data.searchIssuersByTickerSymbol && data.searchIssuersByTickerSymbol.length > 0) {
        onChange(data.searchIssuersByTickerSymbol);
      } else {
        onChange([]);
      }
    },
  });
  const handleSetQueryText = useCallback(
    debounce((queryText) => {
      performSearch({
        variables: {
          vertical: null,
          ...queryVariables,
          tickerSymbol: queryText,
        },
      });
    }, debounceMs),
    [],
  );

  return (
    <div className="relative flex items-center">
      <input
        type="text"
        name="tickerSymbol"
        aria-label="Ticker"
        maxLength={6}
        onKeyDown={(event) => {
          // Only allow alphanumeric characters
          if (!/^[a-zA-Z0-9.]+$/.test(event.key)) {
            event.preventDefault();
          }
          if (event.key === "Enter" && data?.searchIssuersByTickerSymbol?.length === 1) {
            onConfirmSearch?.(data.searchIssuersByTickerSymbol[0]);
          }
        }}
        onChange={(event) => handleSetQueryText(event.target.value)}
        spellCheck={false}
        autoComplete="off"
        placeholder={placeholder}
        disabled={disabled}
        className={classNames(
          "block w-full rounded-md border-0 px-3 py-3 pr-14 text-gray-900 shadow-sm",
          "bg-white ring-1 ring-inset ring-slate-300/60",
          "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-slate-100 focus-visible:ring-offset-1 focus-visible:ring-offset-slate-300",
          "font-mono text-sm",
          "font-bold uppercase tracking-widest text-slate-700",
          "focus:placeholder:font-normal focus:placeholder:normal-case focus:placeholder:tracking-normal",
          "placeholder:font-normal placeholder:normal-case placeholder:tracking-normal placeholder:text-slate-400",
          "focus:placeholder:text-slate-500",
          "disabled:bg-gray-50 disabled:opacity-50",
          "dark:bg-slate-800 dark:text-white dark:ring-slate-950 dark:focus-visible:ring-offset-slate-600",
        )}
      />

      <div className="absolute inset-y-0 right-0 flex space-x-2 py-2 pr-2">
        {loading ? (
          <span className="inline-flex items-center px-3">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" strokeWidth={3} />
          </span>
        ) : (
          <>
            <AnimatePresence>
              {called &&
                variables &&
                variables.tickerSymbol !== "" &&
                (data?.searchIssuersByTickerSymbol || []).length === 0 && (
                  <motion.span
                    initial={{
                      opacity: 0,
                    }}
                    animate={{
                      opacity: 1,
                    }}
                    exit={{
                      opacity: 0,
                    }}
                    className={classNames(
                      "inline-flex items-center rounded-md border border-rose-100 bg-rose-50/50 px-1.5 text-xs font-bold text-rose-500",
                      "dark:border-transparent dark:bg-rose-950/10 dark:text-rose-300",
                    )}
                  >
                    {noResultsText}
                  </motion.span>
                )}
            </AnimatePresence>

            <kbd
              className={classNames(
                "inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 font-mono text-xs font-bold text-slate-500",
                "dark:border-transparent dark:bg-slate-950 dark:text-slate-300",
              )}
            >
              Esc
            </kbd>
          </>
        )}
      </div>
    </div>
  );
}

const searchInputTriggerVariants = cva(
  classNames(
    "group inline-flex w-full gap-x-4 lg:w-auto",
    "relative h-10 items-center bg-white px-3 text-sm font-medium italic text-slate-500",
    "rounded-xl",
    "justify-between",
    "border border-transparent",
    "bg-slate-100 hover:bg-slate-200/70 dark:hover:bg-teal-500/30 dark:hover:text-teal-300",
    "dark:ring-slate-950",
    "dark:border-slate-950 dark:bg-teal-500/20 dark:text-teal-300",
    "focus-visible:outline-none",
    "focus-visible:ring-1 focus-visible:ring-offset-1",
    "focus-visible:ring-blue-300 focus-visible:ring-offset-blue-300",
    "focus-visible:border-transparent focus-visible:bg-blue-50/50 focus-visible:text-slate-500",
    "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
    "whitespace-nowrap disabled:pointer-events-none disabled:cursor-not-allowed",
  ),
  {
    variants: {
      size: {
        big: "h-16 px-4 text-base lg:w-full",
      },
    },
    defaultVariants: {},
  },
);

interface IssuerByTickerSymbolSearchInputTriggerProps extends VariantProps<typeof searchInputTriggerVariants> {
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}
export const IssuerByTickerSymbolSearchInputTrigger = forwardRef<
  HTMLButtonElement,
  IssuerByTickerSymbolSearchInputTriggerProps
>(({ disabled, className, placeholder, size }, ref) => {
  return (
    <DialogTrigger asChild={true}>
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={classNames(searchInputTriggerVariants({ size }), className)}
      >
        {placeholder || <FormattedMessage id="Yj4Jca" defaultMessage="Pesquisar ativos..." />}
        <SearchIcon className="h-5 w-5 opacity-70 group-hover:opacity-100" strokeWidth={2} />
      </button>
    </DialogTrigger>
  );
});

const searchInputButtonTriggerVariants = cva(
  classNames(
    "rounded-xl border border-slate-200 bg-slate-50/60 p-2 text-slate-600",
    "relative dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
    "focus-visible:outline-none",
    "focus-visible:ring-1 focus-visible:ring-offset-1",
    "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
    "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
    "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
  ),
  {
    variants: {
      variant: {
        translucid: classNames(
          "focus-visible:ring-offset-none focus-visible:ring-none border-transparent bg-transparent hover:bg-slate-300/40 focus-visible:bg-blue-50 focus-visible:text-blue-600",
        ),
      },
    },
    defaultVariants: {},
  },
);

interface IssuerByTickerSymbolSearchButtonTriggerProps extends VariantProps<typeof searchInputButtonTriggerVariants> {
  className?: string;
  disabled?: boolean;
}
export const IssuerByTickerSymbolSearchButtonTrigger = forwardRef<
  HTMLButtonElement,
  IssuerByTickerSymbolSearchButtonTriggerProps
>(({ className, disabled, variant }, ref) => {
  return (
    <DialogTrigger asChild={true}>
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={classNames(searchInputButtonTriggerVariants({ variant }), className)}
      >
        <SearchIcon className="h-5 w-5 opacity-70 group-hover:opacity-100" strokeWidth={2} />
      </button>
    </DialogTrigger>
  );
});

interface SelectedIssuersStackProps {
  loading?: boolean;
  disabled?: boolean;
  selectedIssuers: SelectedCompanyIssuer[];
  onChange: (next: SelectedCompanyIssuer[]) => void;
}

export function SelectedIssuersStack({ disabled, loading, selectedIssuers, onChange }: SelectedIssuersStackProps) {
  const intl = useIntl();

  if (selectedIssuers?.length === 0) {
    return null;
  }

  return (
    <ToggleGroup.Root
      loop={true}
      type="multiple"
      disabled={disabled}
      className={classNames(
        "grid h-10 w-full grid-flow-col rounded-xl border border-transparent bg-slate-50 dark:bg-slate-950 dark:ring-slate-950 dark:ring-offset-slate-950",
        "ring-1 ring-slate-200 ring-offset-2 ring-offset-white focus-visible:outline-none",
        "sm:flex sm:w-auto sm:max-w-xs sm:justify-normal sm:space-x-2",
      )}
      orientation="horizontal"
      value={selectedIssuers.map((issuer) => issuer.tickerSymbol)}
      onValueChange={(tickerSymbols) => {
        onChange(
          selectedIssuers.filter((issuer) => {
            return tickerSymbols.includes(issuer.tickerSymbol);
          }),
        );
      }}
    >
      <AnimatePresence>
        {selectedIssuers.map((issuer) => {
          return (
            <ToggleGroup.Item
              key={issuer._id}
              value={issuer.tickerSymbol}
              className={classNames(
                "relative z-0 h-8 w-full items-center justify-center",
                "border border-transparent",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-100 focus-visible:ring-offset-1 focus-visible:ring-offset-gray-300",
                "data-[state=on]:focus-visible:rounded-xl data-[state=on]:focus-visible:bg-gray-50 data-[state=on]:focus-visible:text-gray-600",
              )}
            >
              <motion.div
                key={issuer._id}
                initial={{
                  scale: 0,
                }}
                animate={{
                  scale: 1,
                }}
                exit={{
                  scale: 0,
                }}
                className="relative inline-flex"
              >
                <SimpleTextTooltip content={issuer.name}>
                  <Avatar
                    src={issuer.niceLogo?.url || null}
                    hashValue={issuer._id}
                    alt={issuer.name}
                    size={36}
                    squircle={true}
                    className={classNames("md:inline-flex", {
                      "opacity-10": loading,
                    })}
                  />
                </SimpleTextTooltip>
                {!loading && (
                  <span
                    aria-label={intl.formatMessage({ defaultMessage: "Remover", id: "TolqfU" })}
                    className={classNames(
                      "absolute -right-1 -top-1 z-10 rounded-lg p-0.5",
                      "pointer-events-none border-transparent bg-rose-600 text-white",
                    )}
                  >
                    <XIcon strokeWidth={3} className="h-3 w-3" />
                  </span>
                )}
                {loading && (
                  <span
                    aria-label={intl.formatMessage({ defaultMessage: "Carregando...", id: "EMLr/e" })}
                    className={classNames(
                      "pointer-events-none absolute inset-0 inline-flex h-full w-full animate-pulse",
                      "items-center justify-center rounded-full bg-slate-50 squircle",
                    )}
                  />
                )}
              </motion.div>
            </ToggleGroup.Item>
          );
        })}
      </AnimatePresence>
    </ToggleGroup.Root>
  );
}

function getDescription(vertical?: IPlatformVertical) {
  if (vertical === IPlatformVertical.Stocks) {
    return (
      <FormattedMessage
        defaultMessage="Digite o ticker completo, por exemplo: AAPL, NVDA ou MSFT"
        id="RIXwnW"
        description="Issuer search dialog description - Stocks"
      />
    );
  }

  if (vertical === IPlatformVertical.Reits) {
    return (
      <FormattedMessage
        defaultMessage="Digite o ticker completo, por exemplo: AMT, PLD ou EQIX"
        id="s/U4YN"
        description="Issuer search dialog description - REITs"
      />
    );
  }

  if (vertical === IPlatformVertical.Fiis) {
    return (
      <FormattedMessage
        defaultMessage="Digite o ticker completo, por exemplo: : KNRI11, HGLG11 ou XPML11"
        id="0GR9Ez"
        description="Issuer search dialog description - FIIs"
      />
    );
  }

  return (
    <FormattedMessage
      defaultMessage="Digite o ticker completo, por exemplo: WEGE3, ITUB4 ou TAEE11"
      id="rHjaIH"
      description="Issuer search dialog description - Ações"
    />
  );
}
